import React, { useState, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import {
  MdArrowBack, MdEdit, MdCheck, MdClose, MdDelete, MdSearch, MdVisibility,
  MdGroups, MdPeople,
} from 'react-icons/md';
import { gruposAPI, clientesAPI } from '../services/api';
import { useModal } from '../context/ModalContext';
import DeleteGroupModal from '../components/DeleteGroupModal';

const Container = styled.div`
  max-width: 1100px;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.lg};
  margin-bottom: ${p => p.theme.spacing.xl};
  flex-wrap: wrap;
`;

const BackButton = styled.button`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${p => p.theme.colors.backgroundLight};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  color: ${p => p.theme.colors.textPrimary};
  cursor: pointer;
  transition: ${p => p.theme.transitions.fast};

  svg { font-size: 1.5rem; }

  &:hover {
    border-color: ${p => p.theme.colors.primary};
    color: ${p => p.theme.colors.primary};
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
  flex: 1;
`;

const Title = styled.h1`
  font-size: ${p => p.theme.fontSizes.xxxl};
  color: ${p => p.theme.colors.textPrimary};
  margin: 0;
`;

const TitleInput = styled.input`
  font-size: ${p => p.theme.fontSizes.xxl};
  font-weight: 700;
  padding: 6px 10px;
  border: 1px solid ${p => p.theme.colors.primary};
  border-radius: ${p => p.theme.borderRadius.md};
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary};
`;

const IconBtn = styled.button`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.sm};
  color: ${p => p.theme.colors.textSecondary};
  cursor: pointer;
  transition: ${p => p.theme.transitions.fast};

  svg { font-size: 1rem; }

  &:hover { border-color: ${p => p.theme.colors.primary}; color: ${p => p.theme.colors.primary}; }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.sm};
  margin-left: auto;
  flex-wrap: wrap;
`;

const Button = styled.button`
  padding: 9px 16px;
  background: ${p => p.theme.colors.backgroundLight};
  color: ${p => p.theme.colors.textPrimary};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: ${p => p.theme.transitions.fast};

  &:hover { border-color: ${p => p.theme.colors.primary}; color: ${p => p.theme.colors.primary}; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const PrimaryButton = styled(Button)`
  background: linear-gradient(135deg, ${p => p.theme.colors.primary}, ${p => p.theme.colors.primaryDark});
  color: white;
  border-color: transparent;

  &:hover { color: white; transform: translateY(-1px); }
`;

const DangerButton = styled(Button)`
  background: ${p => p.theme.colors.danger}15;
  color: ${p => p.theme.colors.danger};
  border-color: ${p => p.theme.colors.danger}40;

  &:hover { background: ${p => p.theme.colors.danger}25; border-color: ${p => p.theme.colors.danger}; color: ${p => p.theme.colors.danger}; }
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${p => p.theme.spacing.lg};
  margin-bottom: ${p => p.theme.spacing.xl};
`;

const SummaryCard = styled.div`
  background: ${p => p.theme.colors.backgroundLight};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.xl};
  box-shadow: ${p => p.theme.shadows.card};
`;

const SummaryLabel = styled.div`
  font-size: ${p => p.theme.fontSizes.sm};
  color: ${p => p.theme.colors.textSecondary};
  font-weight: 600;
  margin-bottom: 6px;
`;

const SummaryValue = styled.div`
  font-size: ${p => p.theme.fontSizes.xxl};
  font-weight: 700;
  color: ${p => p.color || p.theme.colors.textPrimary};
`;

const Card = styled.div`
  background: ${p => p.theme.colors.backgroundLight};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.xl};
  box-shadow: ${p => p.theme.shadows.card};
  margin-bottom: ${p => p.theme.spacing.xl};
`;

const CardTitle = styled.h2`
  font-size: ${p => p.theme.fontSizes.xl};
  color: ${p => p.theme.colors.textPrimary};
  margin: 0;
`;

const SearchBox = styled.div`
  position: relative;
  margin-bottom: ${p => p.theme.spacing.sm};
`;

const SearchInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 10px 12px 10px 2.5rem;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  background-color: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary};

  &:focus { border-color: ${p => p.theme.colors.primary}; outline: none; }
`;

const SearchIcon = styled(MdSearch)`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${p => p.theme.colors.textLight};
  font-size: 1.15rem;
`;

const Dropdown = styled.div`
  position: relative;
`;

const DropdownList = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  max-height: 260px;
  overflow-y: auto;
  background: ${p => p.theme.colors.backgroundLight};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  box-shadow: ${p => p.theme.shadows.lg};
  z-index: 20;
`;

const DropdownItem = styled.div`
  padding: 10px 14px;
  cursor: pointer;
  font-size: ${p => p.theme.fontSizes.sm};
  border-bottom: 1px solid ${p => p.theme.colors.borderLight};

  &:last-child { border-bottom: none; }
  &:hover { background: ${p => p.theme.colors.background}; }
`;

const DropdownItemMeta = styled.div`
  font-size: ${p => p.theme.fontSizes.xs};
  color: ${p => p.theme.colors.textLight};
  margin-top: 2px;
`;

const DropdownEmpty = styled.div`
  padding: 14px;
  font-size: ${p => p.theme.fontSizes.sm};
  color: ${p => p.theme.colors.textLight};
  text-align: center;
`;

const ConfirmAddBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${p => p.theme.spacing.md};
  margin-top: ${p => p.theme.spacing.md};
  padding: ${p => p.theme.spacing.md} ${p => p.theme.spacing.lg};
  background: ${p => p.theme.colors.primary}0d;
  border: 1px solid ${p => p.theme.colors.primary}30;
  border-radius: ${p => p.theme.borderRadius.md};
`;

const ConfirmAddText = styled.div`
  font-size: ${p => p.theme.fontSizes.sm};
  color: ${p => p.theme.colors.textPrimary};

  strong { color: ${p => p.theme.colors.primary}; }
`;

const ConfirmAddButtons = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.sm};
`;

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 480px;
`;

const Th = styled.th`
  padding: ${p => p.theme.spacing.md};
  text-align: left;
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: 600;
  color: ${p => p.theme.colors.textSecondary};
  border-bottom: 1px solid ${p => p.theme.colors.border};
`;

const Td = styled.td`
  padding: ${p => p.theme.spacing.md};
  font-size: ${p => p.theme.fontSizes.sm};
  color: ${p => p.theme.colors.textPrimary};
  border-bottom: 1px solid ${p => p.theme.colors.borderLight};
`;

const ClientLink = styled.button`
  color: ${p => p.theme.colors.textPrimary};
  font-weight: 600;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover { color: ${p => p.theme.colors.primary}; text-decoration: underline; }
`;

const RowActions = styled.div`
  display: flex;
  gap: 8px;
`;

const SmallActionButton = styled.button`
  padding: 5px 12px;
  border-radius: 8px;
  font-size: ${p => p.theme.fontSizes.xs};
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  border: none;
  transition: ${p => p.theme.transitions.fast};
  background: ${p => p.danger ? p.theme.colors.danger + '15' : p.theme.colors.primary};
  color: ${p => p.danger ? p.theme.colors.danger : 'white'};

  svg { font-size: 0.9rem; }
`;

const GrupoDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast, confirm } = useModal();

  const [grupo, setGrupo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingName, setEditingName] = useState(false);
  const [nombreDraft, setNombreDraft] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [todosClientes, setTodosClientes] = useState([]);
  const [search, setSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [adding, setAdding] = useState(false);
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const searchBoxRef = useRef(null);

  useEffect(() => {
    loadGrupo();
    clientesAPI.getAll().then(r => setTodosClientes(r.data.data || [])).catch(() => {});
  }, [id]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadGrupo = async () => {
    try {
      setLoading(true);
      const res = await gruposAPI.getById(id);
      setGrupo(res.data.data);
      setNombreDraft(res.data.data.nombre);
    } catch (err) {
      console.error(err);
      toast('Error al cargar el grupo', 'error');
      navigate('/grupos');
    } finally {
      setLoading(false);
    }
  };

  const handleGuardarNombre = async () => {
    if (!nombreDraft.trim()) return;
    setSavingName(true);
    try {
      await gruposAPI.update(id, { nombre: nombreDraft.trim() });
      setEditingName(false);
      loadGrupo();
    } catch (err) {
      toast('Error al renombrar el grupo: ' + (err.message || 'Error desconocido'), 'error');
    } finally {
      setSavingName(false);
    }
  };

  const handleConfirmEliminarGrupo = async (accion) => {
    setShowDeleteModal(false);
    try {
      await gruposAPI.delete(id, accion);
      toast('Grupo eliminado exitosamente', 'success');
      navigate('/grupos');
    } catch (err) {
      toast('Error al eliminar el grupo: ' + (err.message || 'Error desconocido'), 'error');
    }
  };

  const clientesDisponibles = useMemo(() => {
    const term = search.trim().toLowerCase();
    return todosClientes
      .filter(c => c.grupo_id !== grupo?.id)
      .filter(c => !term ||
        c.nombre.toLowerCase().includes(term) ||
        (c.email && c.email.toLowerCase().includes(term)) ||
        (c.telefono && c.telefono.includes(term))
      )
      .slice(0, 30);
  }, [todosClientes, search, grupo]);

  const handleSeleccionarCliente = (cliente) => {
    setClienteSeleccionado(cliente);
    setSearch(cliente.nombre);
    setShowDropdown(false);
  };

  const handleCancelarSeleccion = () => {
    setClienteSeleccionado(null);
    setSearch('');
  };

  const handleConfirmarAgregarCliente = async () => {
    if (!clienteSeleccionado) return;
    setAdding(true);
    try {
      await clientesAPI.update(clienteSeleccionado.id, { grupo_id: id });
      toast(`${clienteSeleccionado.nombre} agregado al grupo`, 'success');
      setSearch('');
      setClienteSeleccionado(null);
      loadGrupo();
      clientesAPI.getAll().then(r => setTodosClientes(r.data.data || [])).catch(() => {});
    } catch (err) {
      toast('Error al agregar el cliente: ' + (err.message || 'Error desconocido'), 'error');
    } finally {
      setAdding(false);
    }
  };

  const handleQuitarCliente = async (cliente) => {
    const ok = await confirm(`¿Quitar a "${cliente.nombre}" de este grupo?`, {
      title: 'Quitar del grupo',
      confirmText: 'Quitar',
    });
    if (!ok) return;
    try {
      await clientesAPI.update(cliente.id, { grupo_id: null });
      toast('Cliente removido del grupo', 'success');
      loadGrupo();
    } catch (err) {
      toast('Error al quitar el cliente: ' + (err.message || 'Error desconocido'), 'error');
    }
  };

  if (loading || !grupo) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '60px' }}>Cargando grupo...</div>
      </Container>
    );
  }

  const saldo = parseFloat(grupo.saldo_pendiente_total || 0);

  return (
    <Container>
      <PageHeader>
        <BackButton onClick={() => navigate('/grupos')}>
          <MdArrowBack />
        </BackButton>

        <TitleRow>
          {editingName ? (
            <>
              <TitleInput
                value={nombreDraft}
                onChange={e => setNombreDraft(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleGuardarNombre()}
                autoFocus
              />
              <IconBtn onClick={handleGuardarNombre} disabled={savingName} title="Guardar">
                <MdCheck />
              </IconBtn>
              <IconBtn onClick={() => { setEditingName(false); setNombreDraft(grupo.nombre); }} title="Cancelar">
                <MdClose />
              </IconBtn>
            </>
          ) : (
            <>
              <Title>{grupo.nombre}</Title>
              <IconBtn onClick={() => setEditingName(true)} title="Renombrar">
                <MdEdit />
              </IconBtn>
            </>
          )}
        </TitleRow>

        <HeaderActions>
          <DangerButton onClick={() => setShowDeleteModal(true)}>
            <MdDelete /> Eliminar Grupo
          </DangerButton>
        </HeaderActions>
      </PageHeader>

      <SummaryGrid>
        <SummaryCard>
          <SummaryLabel>Clientes en el grupo</SummaryLabel>
          <SummaryValue>{grupo.total_clientes}</SummaryValue>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>Saldo Pendiente Combinado</SummaryLabel>
          <SummaryValue color={saldo > 0 ? '#E57373' : '#4CAF50'}>
            ${saldo.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
          </SummaryValue>
        </SummaryCard>
      </SummaryGrid>

      <Card>
        <CardTitle style={{ marginBottom: '14px' }}>Agregar Cliente al Grupo</CardTitle>
        <Dropdown ref={searchBoxRef}>
          <SearchBox>
            <SearchIcon />
            <SearchInput
              type="text"
              placeholder="Busca por nombre, email o teléfono..."
              value={search}
              onChange={e => {
                setSearch(e.target.value);
                setShowDropdown(true);
                if (clienteSeleccionado) setClienteSeleccionado(null);
              }}
              onFocus={() => { if (!clienteSeleccionado) setShowDropdown(true); }}
              disabled={adding}
            />
          </SearchBox>
          {showDropdown && !clienteSeleccionado && (
            <DropdownList>
              {clientesDisponibles.length === 0 ? (
                <DropdownEmpty>
                  {search ? `Sin resultados para "${search}"` : 'No hay más clientes disponibles para agregar'}
                </DropdownEmpty>
              ) : (
                clientesDisponibles.map(c => (
                  <DropdownItem key={c.id} onClick={() => handleSeleccionarCliente(c)}>
                    {c.nombre}
                    {(c.telefono || c.email || c.grupo_nombre) && (
                      <DropdownItemMeta>
                        {c.telefono || c.email || ''}
                        {c.grupo_nombre ? ` · actualmente en: ${c.grupo_nombre}` : ''}
                      </DropdownItemMeta>
                    )}
                  </DropdownItem>
                ))
              )}
            </DropdownList>
          )}
        </Dropdown>

        {clienteSeleccionado && (
          <ConfirmAddBar>
            <ConfirmAddText>
              ¿Agregar a <strong>{clienteSeleccionado.nombre}</strong> a este grupo?
            </ConfirmAddText>
            <ConfirmAddButtons>
              <Button type="button" onClick={handleCancelarSeleccion} disabled={adding}>
                Cancelar
              </Button>
              <PrimaryButton onClick={handleConfirmarAgregarCliente} disabled={adding}>
                {adding ? 'Agregando...' : 'Agregar al grupo'}
              </PrimaryButton>
            </ConfirmAddButtons>
          </ConfirmAddBar>
        )}
      </Card>

      <Card>
        <CardTitle style={{ marginBottom: '14px' }}>
          <MdPeople style={{ verticalAlign: 'middle', marginRight: '6px' }} />
          Clientes ({grupo.clientes.length})
        </CardTitle>

        {grupo.clientes.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
            <MdGroups style={{ fontSize: '2.5rem', opacity: 0.3 }} />
            <p>Este grupo aún no tiene clientes. Agrega uno arriba.</p>
          </div>
        ) : (
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <Th>Cliente</Th>
                  <Th>Contacto</Th>
                  <Th>Saldo Pendiente</Th>
                  <Th>Acciones</Th>
                </tr>
              </thead>
              <tbody>
                {grupo.clientes.map(cliente => (
                  <tr key={cliente.id}>
                    <Td>
                      <ClientLink onClick={() => navigate(`/clientes/${cliente.id}`)}>
                        {cliente.nombre}
                      </ClientLink>
                    </Td>
                    <Td>{cliente.telefono || cliente.email || 'N/A'}</Td>
                    <Td style={{ color: parseFloat(cliente.saldo_pendiente) > 0 ? '#ef4444' : '#10b981', fontWeight: 600 }}>
                      ${parseFloat(cliente.saldo_pendiente || 0).toFixed(2)}
                    </Td>
                    <Td>
                      <RowActions>
                        <SmallActionButton onClick={() => navigate(`/clientes/${cliente.id}`)}>
                          <MdVisibility /> Ver
                        </SmallActionButton>
                        <SmallActionButton danger onClick={() => handleQuitarCliente(cliente)}>
                          <MdClose /> Quitar
                        </SmallActionButton>
                      </RowActions>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
        )}
      </Card>

      {showDeleteModal && (
        <DeleteGroupModal
          grupo={grupo}
          onConfirm={handleConfirmEliminarGrupo}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}
    </Container>
  );
};

export default GrupoDetail;
